const PGAdapter = require('../../common/pg-adapter')
const ProductBaseAdapter = require('./product-base-pg-adapter')
const ProductPresentationAdapter = require('./product-presentation-pg-adapter')
const getProductSkuName = require('../tools/get-productsku-name')

const PRODUCT_SKU_TABLE_NAME = 'avocado.data_productsku'
const PRODUCT_SKU_COLUMNS = [
  'created_at',
  'updated_at',
  'id',
  'sku_id',
  'old_sku_id',
  'order_sku',
  'market_id',
  'name',
  'display_name',
  'inventory_sensitivity',
  'display_order',
  'phase',
  'available',
  'ready',
  'promoted',
  'ppmv',
  'ppmv_core',
  'supplier',
  'visibility',
  'markup',
  'vat',
  'presentation_id',
  'eligible_for_od',
  'eligible_for_pays',
  'segments',
  '_deleted',
  'essential_medicines'
]

const searchColumns = [
  'display_name',
  'old_sku_id'
]

class ProductSKUAdapter extends PGAdapter {
  constructor (logger, pgConnection, username) {
    const opts = {
      strictColumns: false,
      addUuid: false,
      searchColumns
    }
    super(
      pgConnection,
      PRODUCT_SKU_TABLE_NAME,
      username,
      PRODUCT_SKU_COLUMNS,
      'id',
      {},
      logger,
      opts
    )
    this.logger = logger
    this.pgConnection = pgConnection
    this.username = username
  }

  async create (data) {
    // We need to build the display name for the product before creating new product sku row

    const displayName = await this.generateDisplayName(data)

    const rowToCreate = {...data, display_name: displayName}

    const row = await super.create(rowToCreate)

    return row
  }

  async generateDisplayName (data) {
    const { presentation_id: presentationId } = data

    const presentationAdapter = new ProductPresentationAdapter(this.logger, this.pgConnection, this.username)

    const {
      product_id: baseId,
      presentation_qty: presentationQty,
      presentation_unit: presentationUnit,
      presentation_type: presentationType
    } = await presentationAdapter.getOne(presentationId)

    const baseAdapter = new ProductBaseAdapter(this.logger, this.pgConnection, this.username)

    const {
      name,
      variety,
      dosage_qty: dosageQty,
      dosage_unit: dosageUnit
    } = await baseAdapter.getOne(baseId)

    const displayName = getProductSkuName({
      presentationQty,
      presentationUnit,
      presentationType,
      name,
      variety,
      dosageQty,
      dosageUnit
    })

    return displayName
  }

  async uploadProductAvailability (data) {
    if (!this.pgConnection) {
      throw new Error('you can only upload from admin tool')
    }
    const newData = Object.values(data)
    newData.forEach(async element => {
      const {old_sku_id: sku, available} = element
      const query = `UPDATE avocado.data_productsku SET available = $1 WHERE old_sku_id = $2`
      const value = [available, sku]

      try {
        const result = await this.pgConnection.query(query, value)
        return result.rows
      } catch (err) {
        throw err
      }
    }
    )
  }

  async getGenericIdsForSkus (skus = []) {
    if (!this.pgConnection || !skus.length) {
      return {}
    }

    const normalizedSkus = skus.map(sku => (
      typeof sku === 'string' && sku.startsWith('product:') ? sku.replace('product:', '') : sku
    ))

    const { rows } = await this.pgConnection.query(
      `
        SELECT
          bs.old_sku_id,
          bs.display_name,
          vp.name AS generic_name,
          vp.generic_id,
          pr.presentation_qty AS unit_of_reporting,
          pr_parent.presentation_qty AS unit_of_issue,
          pr_parent.presentation_type,
          pr_parent.packaging_type,
          ps_generic.presentation_unit AS generic_presentation
        FROM avocado.data_productsku bs
        LEFT JOIN avocado.data_productpack pr
          ON pr.parent_presentation_id = bs.presentation_id
        LEFT JOIN avocado.data_productpack pr_parent
          ON pr_parent.id = bs.presentation_id
        LEFT JOIN avocado.data_productbase pb
          ON pb.id = pr_parent.product_id
        LEFT JOIN avocado.data_virtual_product vp
          ON vp.id = pb.virtual_product_id
        LEFT JOIN avocado.data_productsku ps
          ON ps.old_sku_id = vp.generic_id
        LEFT JOIN avocado.data_productpack ps_generic
          ON ps_generic.id = ps.presentation_id
        WHERE bs.old_sku_id = ANY($1)
      `,
      [normalizedSkus]
    )

    return rows.reduce((acc, row) => {
      const sku = row.old_sku_id
      if (sku) {
        // For certain skus who are their own generic parent we default to 1 so the generic factor deafaults to 1
        const unitOfIssue = row.unit_of_issue != null ? Number(row.unit_of_issue) : 1
        const unitOfReporting = row.unit_of_reporting != null ? Number(row.unit_of_reporting) : 1
        const genericFactor = unitOfIssue / unitOfReporting
        acc[`product:${sku}`] = {
          name: row.display_name || null,
          genericName: row.generic_name || null,
          genericParent: row.generic_id || null,
          genericFactor,
          unitOfIssue,
          unitOfReporting,
          presentationType: row.presentation_type || null,
          packagingType: row.packaging_type || null,
          genericPresentation: row.generic_presentation || null
        }
      }
      return acc
    }, {})
  }

  // Read the columns a product doc is built from, for the ids of the couch
  // products synced into PG by CouchProductToPg. `old_sku_id` is the couch
  // `_id` minus its `product:` prefix, and the unit-of-reporting sku carries a
  // `-<basicUnit>` suffix, so an exact match lands on the unit-of-issue sku the
  // way a couch read by `_id` does.
  //
  // Shaping the rows into product docs is `pgRowsToProducts`, kept separate so
  // it can be tested without a database.
  async getProductRowsForSkus (skus = []) {
    if (!this.pgConnection || !skus.length) {
      return []
    }

    const normalizedSkus = skus.map(sku => (
      typeof sku === 'string' && sku.startsWith('product:') ? sku.replace('product:', '') : sku
    ))

    const { rows } = await this.pgConnection.query(
      `
        SELECT
          ps.old_sku_id,
          ps.name,
          ps.display_name,
          pres.volume,
          pres.presentation_qty AS unit_of_issue,
          pres.packaging_type,
          pb.manufacturer
        FROM avocado.data_productsku ps
        LEFT JOIN avocado.data_productpack pres
          ON pres.id = ps.presentation_id
        LEFT JOIN avocado.data_productbase pb
          ON pb.id = pres.product_id
        WHERE ps.old_sku_id = ANY($1)
          AND ps._deleted IS NOT TRUE
      `,
      [normalizedSkus]
    )

    return rows
  }
}

module.exports = ProductSKUAdapter
